/**
 * Worth a look — the club's most notable recent moments, served by
 * /api/v1/fixtures/highlights (strongest by score across the last five
 * filmed matches the club played, at most two per type). side 'ours' is
 * the club's own team; 'opponents' the teams it played, as a second strip
 * (Ben, 2026-10-27: the board was mostly other teams' moments). Each row opens the match page ON
 * that moment — ?moment= plus ?type=, because without the type the match
 * page boots on its default filter and the deep link falls back to the
 * nearest same-filter moment instead (Ben, 2026-10-26: a 66 supportive
 * pass opened a 14 progressive pass).
 *
 * Additive by design: no data, an error, or a club with no film renders
 * nothing at all — the pages it sits on lose nothing when it is quiet.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import { pathSeg } from '../lib/urls'
import './worth-a-look.css'

interface Highlight {
  /** stable identity; the link still rides game_code because the match
   *  page's address is code-based */
  game_id: number
  game_code: string
  home_team: string
  away_team: string
  date: string | null
  moment_id: string
  moment_type: string
  score: number | null
  player_jersey: number | null
  to_player_jersey: number | null
  zone: string | null
  in_behind: boolean
  frame: number | null
  /** Minutes into the MATCH as a clock string ('9', '45+2'), computed
   *  server-side from the kickoff/half-time marks — never a video minute. */
  minute: string | null
}

const TYPE_LABEL: Record<string, string> = {
  progressive_pass: 'Progressive pass',
  supportive_pass: 'Supportive pass',
  progressive_run: 'Progressive run',
  supportive_run: 'Supportive run',
  pressing: 'Press',
  box_entry_cci: 'Box entry',
}

const COPY = {
  ours: {
    title: 'Worth a look',
    sub: 'Your team’s strongest moments from your last five filmed matches — each one opens the match at that moment.',
  },
  opponents: {
    title: 'Opponents worth a look',
    sub: 'What the teams you played did best in those matches.',
  },
}

export default function WorthALook({ limit = 6, side = 'ours' }: {
  limit?: number
  side?: 'ours' | 'opponents'
}) {
  const [items, setItems] = useState<Highlight[] | null>(null)

  useEffect(() => {
    // the server's default is ours; only the opponents strip names a side
    const q = side === 'opponents' ? '&side=opponents' : ''
    api<{ highlights: Highlight[] }>(`/api/v1/fixtures/highlights?limit=${limit}${q}`)
      .then((d) => setItems(d.highlights))
      // quiet on failure: this strip is a bonus, never a blocker
      .catch(() => setItems(null))
  }, [limit, side])

  if (!items?.length) return null

  return (
    <section className="wal">
      <div className="wal__head">
        <h2 className="wal__title">{COPY[side].title}</h2>
        <span className="wal__sub">{COPY[side].sub}</span>
      </div>
      <div className="wal__grid">
        {items.map((h) => (
          // moment_id is unique only per game (idx_moments_unique_id is
          // (game_id, moment_id)); two matches both offer a 'box_cci_1'
          <Link key={`${h.game_id}:${h.moment_id}`}
                className="wal__item"
                to={`/match-room/${pathSeg(h.game_code)}?moment=${encodeURIComponent(h.moment_id)}&type=${encodeURIComponent(h.moment_type)}${h.frame != null ? `&frame=${h.frame}` : ''}${side === 'opponents' ? '&team=opponents' : ''}`}>
            <span className="wal__type">{TYPE_LABEL[h.moment_type] ?? h.moment_type.replace(/_/g, ' ')}</span>
            <span className="wal__who">
              {h.player_jersey != null ? `#${h.player_jersey}` : 'Team'}
              {h.to_player_jersey != null ? ` → #${h.to_player_jersey}` : ''}
              {h.minute != null ? ` · ${h.minute}${/^\d/.test(h.minute) ? "'" : ''}` : ''}
              {h.in_behind ? ' · in behind' : ''}
            </span>
            <span className="wal__match">
              {h.away_team} at {h.home_team}
              {/* The 0-100 integer the moment cards' rings show — "91",
                  never "0.91" (Ben, 2026-10-26: one number, both places). */}
              {h.score != null && (
                <span className="wal__score" title="Moment score, 0 to 100 within its type">
                  {Math.round(h.score * 100)}
                </span>
              )}
            </span>
          </Link>
        ))}
      </div>
    </section>
  )
}
